import {
  BOOST_ACCELERATION,
  BOOST_DURATION,
  FIXED_TIMESTEP,
  GRAVITY,
  MAX_FRAME_DELTA,
  RESET_LOCK_SECONDS,
} from '../core/constants';
import type {
  InputState,
  VehicleState,
  VehicleTelemetry,
  VectorState,
} from '../core/types';
import type {
  TrackBooster,
  TrackNearestPoint,
  TrackSurfaceSample,
  TrackTransform,
  VehicleControllerOptions,
  VehicleRecoverySignal,
  VehicleRecoveryStatus,
  VehicleStepContext,
  VehicleTrackQuery,
  VehicleUpdateResult,
} from './contracts';
import { NEUTRAL_INPUT } from './contracts';
import {
  clamp,
  clamp01,
  copyVector,
  damp,
  dot,
  finiteOr,
  horizontalLength,
  horizontalNormalize,
  moveTowards,
  planarDistance,
  yawForward,
  yawRight,
} from './math';

// Generated car roots sit at the tyre contact plane. The renderer adds a tiny
// visual clearance, so the simulation itself grounds its origin on the road.
const DEFAULT_RIDE_HEIGHT = 0;
const DEFAULT_OFF_TRACK_GRACE = 1.1;
const DEFAULT_UPSIDE_DOWN_GRACE = 1.8;
const DEFAULT_PLAYABLE_MARGIN = 18;
const RELATIVE_FALL_DISTANCE = 30;

interface JumpLaunchTuning {
  id: string;
  gapStart: number;
  gapEnd: number;
  gapLength: number;
  landingBuffer: number;
  launchImpulse: number;
  minimumLaunchSpeed: number;
  landingHeight: number;
}

type JumpSurfaceSample = TrackSurfaceSample & {
  jump?: JumpLaunchTuning | null;
};

function cloneState(state: VehicleState): VehicleState {
  return {
    ...state,
    position: copyVector(state.position),
    velocity: copyVector(state.velocity),
  };
}

function normalizeInput(input: InputState): InputState {
  return {
    throttle: clamp01(finiteOr(input.throttle, 0)),
    brake: clamp01(finiteOr(input.brake, 0)),
    steer: clamp(finiteOr(input.steer, 0), -1, 1),
    handbrake: Boolean(input.handbrake),
    restartPressed: Boolean(input.restartPressed),
    resetPressed: Boolean(input.resetPressed),
    pausePressed: Boolean(input.pausePressed),
  };
}

/**
 * Deterministic, fixed-step arcade vehicle simulation.
 *
 * This is intentionally renderer-agnostic. A scene copies the returned pose to
 * its mesh, while a procedural track only needs to implement VehicleTrackQuery.
 */
export class VehicleController {
  readonly fixedTimestep: number;

  private readonly physics: VehicleControllerOptions['physics'];
  private track: VehicleTrackQuery;
  private readonly rideHeight: number;
  private state: VehicleState;
  private accumulator = 0;
  private controlLockSeconds = 0;
  private boostAcceleration = BOOST_ACCELERATION;
  private boostDuration = BOOST_DURATION;
  private boostSpeedCap = 0;
  private groundIgnoreSeconds = 0;
  private boosterContacts = new Set<string>();
  private launchContacts = new Set<string>();
  private lastBoosterTriggered: string | null = null;
  private lastLandingImpact = 0;
  private offTrackSeconds = 0;
  private upsideDownSeconds = 0;
  private onTrack = true;
  private inPlayableArea = true;
  private recoverySignal: VehicleRecoverySignal | null = null;
  private lastSafeSurfaceY: number;

  constructor(options: VehicleControllerOptions) {
    this.physics = { ...options.physics };
    this.track = options.track;
    this.fixedTimestep = Math.max(1 / 300, finiteOr(options.fixedTimestep, FIXED_TIMESTEP));
    this.rideHeight = Math.max(0, finiteOr(options.rideHeight, DEFAULT_RIDE_HEIGHT));

    const transform = options.initialTransform;
    const progress = transform.progress ?? this.safeNearest(transform.position, 0).progress;
    this.state = {
      position: copyVector(transform.position),
      velocity: { x: 0, y: 0, z: 0 },
      yaw: finiteOr(transform.yaw, 0),
      pitch: finiteOr(transform.pitch, 0),
      roll: finiteOr(transform.roll, 0),
      verticalVelocity: 0,
      grounded: true,
      progress,
      sampleIndex: Math.max(0, Math.floor(finiteOr(transform.sampleIndex, 0))),
      sliding: 0,
      boostTimer: 0,
    };
    this.lastSafeSurfaceY = transform.position.y - this.rideHeight;
    this.snapToTrackIfClose();
  }

  /** Advances a frame; internally all physics runs at the fixed timestep. */
  update(
    input: InputState = NEUTRAL_INPUT as InputState,
    frameDeltaSeconds: number,
    context: VehicleStepContext = {},
  ): VehicleUpdateResult {
    this.lastBoosterTriggered = null;
    this.lastLandingImpact = 0;

    if (context.paused || !Number.isFinite(frameDeltaSeconds) || frameDeltaSeconds <= 0) {
      return this.updateResult(0);
    }

    const delta = Math.min(MAX_FRAME_DELTA, frameDeltaSeconds);
    this.accumulator += delta;
    const controlsEnabled = context.controlsEnabled ?? true;
    const normalizedInput = controlsEnabled ? normalizeInput(input) : (NEUTRAL_INPUT as InputState);
    let steps = 0;

    while (this.accumulator + 1e-10 >= this.fixedTimestep) {
      this.fixedUpdate(normalizedInput, controlsEnabled, this.fixedTimestep);
      this.accumulator -= this.fixedTimestep;
      steps += 1;
    }

    // Avoid a tiny negative accumulator after an exact series of fixed steps.
    if (this.accumulator < 0) this.accumulator = 0;
    return this.updateResult(steps);
  }

  /** Alias used by game-loop code that calls simulation systems `advance`. */
  advance(
    input: InputState,
    frameDeltaSeconds: number,
    context: VehicleStepContext = {},
  ): VehicleUpdateResult {
    return this.update(input, frameDeltaSeconds, context);
  }

  getState(): VehicleState {
    return cloneState(this.state);
  }

  getTelemetry(): VehicleTelemetry {
    const forward = yawForward(this.state.yaw);
    const forwardSpeed = dot(this.state.velocity, forward);
    return {
      speedMps: Math.hypot(this.state.velocity.x, this.state.velocity.y, this.state.velocity.z),
      forwardSpeed,
      grounded: this.state.grounded,
      sliding: this.state.sliding,
      boostAmount:
        this.boostDuration > 0 ? clamp01(this.state.boostTimer / this.boostDuration) : 0,
      position: copyVector(this.state.position),
      yaw: this.state.yaw,
    };
  }

  getRecoveryStatus(): VehicleRecoveryStatus {
    return {
      onTrack: this.onTrack,
      inPlayableArea: this.inPlayableArea,
      offTrackSeconds: this.offTrackSeconds,
      upsideDownSeconds: this.upsideDownSeconds,
      pending: this.recoverySignal
        ? { ...this.recoverySignal, position: copyVector(this.recoverySignal.position) }
        : null,
    };
  }

  consumeRecoverySignal(): VehicleRecoverySignal | null {
    if (!this.recoverySignal) return null;
    const signal = {
      ...this.recoverySignal,
      position: copyVector(this.recoverySignal.position),
    };
    this.recoverySignal = null;
    return signal;
  }

  isControlLocked(): boolean {
    return this.controlLockSeconds > 0;
  }

  /** Replaces the track query without coupling the controller to track meshes. */
  setTrack(track: VehicleTrackQuery): void {
    this.track = track;
    this.boosterContacts.clear();
    this.launchContacts.clear();
    this.snapToTrackIfClose();
  }

  /**
   * Fast restart/reset entry point. Velocities and dangerous orientation are
   * cleared, then controls stay locked briefly so the transform settles.
   */
  resetTo(transform: TrackTransform, lockSeconds = RESET_LOCK_SECONDS): void {
    const nearest = this.safeNearest(transform.position, transform.progress ?? this.state.progress);
    this.state.position = copyVector(transform.position);
    this.state.velocity = { x: 0, y: 0, z: 0 };
    this.state.verticalVelocity = 0;
    this.state.yaw = finiteOr(transform.yaw, 0);
    this.state.pitch = finiteOr(transform.pitch, 0);
    this.state.roll = finiteOr(transform.roll, 0);
    this.state.grounded = true;
    this.state.progress = transform.progress ?? nearest.progress;
    this.state.sampleIndex = Math.max(
      0,
      Math.floor(finiteOr(transform.sampleIndex, nearest.sample.sampleIndex ?? 0)),
    );
    this.state.sliding = 0;
    this.state.boostTimer = 0;
    this.boostAcceleration = BOOST_ACCELERATION;
    this.boostDuration = BOOST_DURATION;
    this.boostSpeedCap = 0;
    this.groundIgnoreSeconds = 0;
    this.accumulator = 0;
    this.controlLockSeconds = Math.max(0, finiteOr(lockSeconds, RESET_LOCK_SECONDS));
    this.boosterContacts.clear();
    this.launchContacts.clear();
    this.lastBoosterTriggered = null;
    this.lastLandingImpact = 0;
    this.offTrackSeconds = 0;
    this.upsideDownSeconds = 0;
    this.recoverySignal = null;
    this.onTrack = true;
    this.inPlayableArea = true;
    this.lastSafeSurfaceY = transform.position.y - this.rideHeight;
    this.snapToTrackIfClose();
  }

  restartAt(transform: TrackTransform): void {
    this.resetTo(transform, 0);
  }

  /** Teleports a vehicle while optionally preserving its momentum. */
  teleport(
    transform: TrackTransform,
    options: { preserveVelocity?: boolean; lockSeconds?: number } = {},
  ): void {
    const velocity = copyVector(this.state.velocity);
    this.resetTo(transform, options.lockSeconds ?? 0);
    if (options.preserveVelocity) {
      this.state.velocity = velocity;
      this.state.verticalVelocity = velocity.y;
    }
  }

  applyImpulse(impulse: VectorState): void {
    this.state.velocity.x += finiteOr(impulse.x, 0);
    this.state.velocity.y += finiteOr(impulse.y, 0);
    this.state.velocity.z += finiteOr(impulse.z, 0);
    this.state.verticalVelocity = this.state.velocity.y;
    if (Math.abs(impulse.y) > 0.05) this.state.grounded = false;
  }

  /**
   * Activates a deterministic track boost. Calling it again only refreshes the
   * controlled boost window; it never stacks unbounded speed.
   */
  activateBooster(booster: TrackBooster | string = 'external'): void {
    const definition =
      typeof booster === 'string'
        ? this.track.boosters?.find((candidate) => candidate.id === booster) ?? {
            id: booster,
            progress: this.state.progress,
          }
        : booster;

    const duration = Math.max(this.fixedTimestep, finiteOr(definition.duration, BOOST_DURATION));
    const acceleration = Math.max(0, finiteOr(definition.acceleration, BOOST_ACCELERATION));
    const defaultCap = Math.max(1, this.physics.maxSpeed) * 1.22;
    this.boostDuration = duration;
    this.boostAcceleration = acceleration;
    this.boostSpeedCap = Math.max(
      this.physics.maxSpeed,
      finiteOr(definition.maxSpeed, defaultCap),
    );
    this.state.boostTimer = Math.max(this.state.boostTimer, duration);

    const minimumSpeed = Math.max(0, finiteOr(definition.minimumSpeed, 0));
    if (minimumSpeed > 0) {
      const forward = yawForward(this.state.yaw);
      const currentForwardSpeed = dot(this.state.velocity, forward);
      if (currentForwardSpeed < minimumSpeed) {
        const increase = minimumSpeed - currentForwardSpeed;
        this.state.velocity.x += forward.x * increase;
        this.state.velocity.z += forward.z * increase;
      }
    }
    this.lastBoosterTriggered = definition.id;
  }

  private fixedUpdate(input: InputState, controlsEnabled: boolean, deltaSeconds: number): void {
    this.groundIgnoreSeconds = Math.max(0, this.groundIgnoreSeconds - deltaSeconds);
    const before = this.safeNearest(this.state.position, this.state.progress);
    const beforeRoad = this.roadStatus(before);
    this.updateLaunchContacts(before.sample);
    this.state.progress = before.progress;
    this.state.sampleIndex = before.sample.sampleIndex ?? this.state.sampleIndex;

    if (this.controlLockSeconds > 0) {
      this.controlLockSeconds = Math.max(0, this.controlLockSeconds - deltaSeconds);
      this.state.velocity = { x: 0, y: 0, z: 0 };
      this.state.verticalVelocity = 0;
      this.state.sliding = damp(this.state.sliding, 0, 14, deltaSeconds);
      this.settleLockedVehicle(before, beforeRoad.onRoad);
      this.updateBoosterContacts(false);
      this.updateRecovery(before, beforeRoad.onRoad, beforeRoad.inPlayableArea, deltaSeconds);
      return;
    }

    this.updateBoosterContacts();
    const wasGrounded = this.state.grounded;
    const priorVerticalSpeed = this.state.velocity.y;
    const speed = Math.hypot(this.state.velocity.x, this.state.velocity.z);
    const speedRatio = clamp01(speed / Math.max(1, this.physics.maxSpeed));

    if (wasGrounded && beforeRoad.onRoad && before.sample.driveable !== false) {
      this.updateGrounded(input, controlsEnabled, before.sample, speedRatio, deltaSeconds);
    } else {
      this.state.grounded = false;
      this.updateAirborne(input, controlsEnabled, speedRatio, deltaSeconds);
    }

    this.state.position.x += this.state.velocity.x * deltaSeconds;
    this.state.position.y += this.state.velocity.y * deltaSeconds;
    this.state.position.z += this.state.velocity.z * deltaSeconds;

    let after = this.safeNearest(this.state.position, before.progress);
    if (this.resolveBarrierCollision(after)) {
      after = this.safeNearest(this.state.position, after.progress);
    }
    const afterRoad = this.roadStatus(after);
    this.state.progress = after.progress;
    this.state.sampleIndex = after.sample.sampleIndex ?? this.state.sampleIndex;
    this.resolveGrounding(before.sample, after.sample, afterRoad.onRoad, wasGrounded, priorVerticalSpeed, deltaSeconds);
    this.updateBoosterContacts();
    this.updateRecovery(after, afterRoad.onRoad, afterRoad.inPlayableArea, deltaSeconds);
  }

  private updateGrounded(
    input: InputState,
    controlsEnabled: boolean,
    sample: TrackSurfaceSample,
    speedRatio: number,
    deltaSeconds: number,
  ): void {
    const oldHorizontalVelocity = {
      x: this.state.velocity.x,
      y: 0,
      z: this.state.velocity.z,
    };
    const oldForward = yawForward(this.state.yaw);
    const oldForwardSpeed = dot(oldHorizontalVelocity, oldForward);
    const steeringRate =
      this.physics.steeringRate +
      (this.physics.highSpeedSteering - this.physics.steeringRate) * speedRatio;
    const lowSpeedAuthority = clamp(Math.abs(oldForwardSpeed) / 3.5, 0.16, 1);
    const reverseDirection = oldForwardSpeed < -0.25 ? -1 : 1;
    const handbrakeTurn = input.handbrake ? 1.18 : 1;

    if (controlsEnabled) {
      this.state.yaw +=
        input.steer *
        Math.max(0, steeringRate) *
        lowSpeedAuthority *
        reverseDirection *
        handbrakeTurn *
        deltaSeconds;
    }

    const forward = yawForward(this.state.yaw);
    const right = yawRight(this.state.yaw);
    let forwardSpeed = dot(oldHorizontalVelocity, forward);
    let lateralSpeed = dot(oldHorizontalVelocity, right);
    const speedBeforeForces = forwardSpeed;

    if (controlsEnabled) {
      if (input.throttle > 0) {
        forwardSpeed += Math.max(0, this.physics.acceleration) * input.throttle * deltaSeconds;
      }

      if (input.brake > 0) {
        if (forwardSpeed > 0.35) {
          forwardSpeed = moveTowards(
            forwardSpeed,
            0,
            Math.max(0, this.physics.brakePower) * input.brake * deltaSeconds,
          );
        } else {
          forwardSpeed -=
            Math.max(0, this.physics.reverseAcceleration) * input.brake * deltaSeconds;
        }
      }
    }

    if ((!controlsEnabled || input.throttle <= 0) && (!controlsEnabled || input.brake <= 0)) {
      forwardSpeed = moveTowards(
        forwardSpeed,
        0,
        Math.max(0, this.physics.rollingResistance) * deltaSeconds,
      );
    }

    const aerodynamicDrag = Math.max(0, this.physics.aerodynamicDrag);
    if (aerodynamicDrag > 0) {
      const drag = aerodynamicDrag * forwardSpeed * Math.abs(forwardSpeed) * deltaSeconds;
      forwardSpeed = Math.abs(drag) >= Math.abs(forwardSpeed) ? 0 : forwardSpeed - drag;
    }

    if (this.state.boostTimer > 0) {
      forwardSpeed += this.boostAcceleration * deltaSeconds;
      this.state.boostTimer = Math.max(0, this.state.boostTimer - deltaSeconds);
    }

    const activeSpeedCap =
      this.state.boostTimer > 0
        ? Math.max(this.physics.maxSpeed, this.boostSpeedCap)
        : Math.max(1, this.physics.maxSpeed);
    // Speed gained from a pad decays naturally through drag instead of being
    // chopped back to the ordinary cap on the first non-boost frame.
    const forwardSpeedCap = Math.max(activeSpeedCap, speedBeforeForces);
    forwardSpeed = clamp(
      forwardSpeed,
      -Math.max(0, this.physics.maxReverseSpeed),
      forwardSpeedCap,
    );

    const grip = input.handbrake
      ? Math.max(0.05, this.physics.handbrakeGrip)
      : Math.max(0.05, this.physics.lateralGrip);
    lateralSpeed *= Math.exp(-grip * deltaSeconds);
    if (input.handbrake) {
      forwardSpeed = moveTowards(
        forwardSpeed,
        0,
        Math.max(0, this.physics.rollingResistance) * 0.6 * deltaSeconds,
      );
    }

    this.state.velocity.x = forward.x * forwardSpeed + right.x * lateralSpeed;
    this.state.velocity.z = forward.z * forwardSpeed + right.z * lateralSpeed;

    const trackForward = horizontalNormalize(sample.forward);
    const alongTrackSpeed = dot(this.state.velocity, trackForward);
    const horizontalTangentLength = Math.max(0.05, horizontalLength(sample.forward));
    this.state.velocity.y = (sample.forward.y / horizontalTangentLength) * alongTrackSpeed;
    this.state.verticalVelocity = this.state.velocity.y;

    const slipRatio = Math.abs(lateralSpeed) / Math.max(3, Math.abs(forwardSpeed));
    this.state.sliding = damp(this.state.sliding, clamp01(slipRatio * 1.8), 10, deltaSeconds);

    const stability = Math.max(0.1, this.physics.stability);
    const pitchTarget = Math.atan2(sample.forward.y, horizontalTangentLength);
    const bankTarget = finiteOr(sample.bank, 0);
    const steeringLean = -input.steer * speedRatio * 0.08;
    if (Math.abs(this.state.pitch) < 1.25) {
      this.state.pitch = damp(this.state.pitch, pitchTarget, stability, deltaSeconds);
    }
    if (Math.abs(this.state.roll) < 1.25) {
      this.state.roll = damp(this.state.roll, bankTarget + steeringLean, stability, deltaSeconds);
    }
  }

  private updateAirborne(
    input: InputState,
    controlsEnabled: boolean,
    speedRatio: number,
    deltaSeconds: number,
  ): void {
    const airControl = Math.max(0, this.physics.airControl);
    if (controlsEnabled) {
      this.state.yaw += input.steer * airControl * 0.5 * deltaSeconds;
      this.state.pitch += (input.brake - input.throttle) * airControl * 0.12 * deltaSeconds;
      this.state.roll -= input.steer * airControl * 0.22 * deltaSeconds;
    }

    const downforce = Math.max(0, this.physics.downforce) * speedRatio * 0.18;
    if (this.state.boostTimer > 0) {
      const forward = yawForward(this.state.yaw);
      this.state.velocity.x += forward.x * this.boostAcceleration * deltaSeconds;
      this.state.velocity.z += forward.z * this.boostAcceleration * deltaSeconds;
      const horizontalSpeed = Math.hypot(this.state.velocity.x, this.state.velocity.z);
      const cap = Math.max(this.physics.maxSpeed, this.boostSpeedCap);
      if (horizontalSpeed > cap && horizontalSpeed > 0) {
        const scale = cap / horizontalSpeed;
        this.state.velocity.x *= scale;
        this.state.velocity.z *= scale;
      }
    }
    this.state.velocity.y -= (GRAVITY + downforce) * deltaSeconds;
    this.state.verticalVelocity = this.state.velocity.y;
    this.state.boostTimer = Math.max(0, this.state.boostTimer - deltaSeconds);

    // Stability corrects normal jump attitude, but never magically rights a
    // fully inverted, stopped vehicle; that case belongs to checkpoint reset.
    if (Math.abs(this.state.pitch) < 1.25 && Math.abs(this.state.roll) < 1.25) {
      const airStability = Math.max(0, this.physics.stability) * 0.22;
      this.state.pitch = damp(this.state.pitch, 0, airStability, deltaSeconds);
      this.state.roll = damp(this.state.roll, 0, airStability, deltaSeconds);
    }
    this.state.sliding = damp(this.state.sliding, 0, 3, deltaSeconds);
  }

  private resolveGrounding(
    beforeSample: TrackSurfaceSample,
    sample: TrackSurfaceSample,
    onRoad: boolean,
    wasGrounded: boolean,
    priorVerticalSpeed: number,
    deltaSeconds: number,
  ): void {
    const surfaceY = this.surfaceHeight(sample, this.state.position);
    const clearance = this.state.position.y - surfaceY;
    const priorSlope = beforeSample.forward.y / Math.max(0.05, horizontalLength(beforeSample.forward));
    const nextSlope = sample.forward.y / Math.max(0.05, horizontalLength(sample.forward));
    const speedRatio = clamp01(
      Math.hypot(this.state.velocity.x, this.state.velocity.z) / Math.max(1, this.physics.maxSpeed),
    );
    const jump = (sample as JumpSurfaceSample).jump ?? (beforeSample as JumpSurfaceSample).jump;
    const enteringGap =
      wasGrounded &&
      sample.driveable === false &&
      (sample.launch === true || beforeSample.launch === true) &&
      (!jump || !this.launchContacts.has(jump.id));

    if (enteringGap) {
      if (jump) {
        this.launchContacts.add(jump.id);
        this.applyGapLaunch(sample, jump, speedRatio);
      }
      this.state.grounded = false;
      this.groundIgnoreSeconds = 0.14;
      this.state.verticalVelocity = this.state.velocity.y;
      return;
    }

    const leavingRamp =
      wasGrounded &&
      this.state.velocity.y > 1.2 &&
      priorSlope - nextSlope > 0.075;

    if (leavingRamp) {
      this.state.grounded = false;
      this.groundIgnoreSeconds = 0.14;
      return;
    }

    const adhesion = 0.1 + Math.max(0, this.physics.downforce) * speedRatio * deltaSeconds * 0.14;
    const canSnapWhileGrounded =
      wasGrounded && clearance <= Math.max(0.16, adhesion) && clearance >= -0.8;
    const canLand =
      !wasGrounded &&
      this.state.velocity.y <= 0 &&
      clearance <= 0.2 &&
      clearance >= -0.8;

    if (
      onRoad &&
      sample.driveable !== false &&
      this.groundIgnoreSeconds <= 0 &&
      (canSnapWhileGrounded || canLand)
    ) {
      if (!wasGrounded && canLand) {
        this.lastLandingImpact = Math.max(
          this.lastLandingImpact,
          Math.max(0, -Math.min(priorVerticalSpeed, this.state.velocity.y)),
        );
      }
      this.state.position.y = surfaceY;
      this.state.grounded = true;
      const trackForward = horizontalNormalize(sample.forward);
      const alongTrackSpeed = dot(this.state.velocity, trackForward);
      this.state.velocity.y =
        (sample.forward.y / Math.max(0.05, horizontalLength(sample.forward))) * alongTrackSpeed;
      this.state.verticalVelocity = this.state.velocity.y;
      this.lastSafeSurfaceY = sample.position.y;
      return;
    }

    this.state.grounded = false;
    this.state.verticalVelocity = this.state.velocity.y;
  }

  private applyGapLaunch(
    sample: TrackSurfaceSample,
    jump: JumpLaunchTuning,
    speedRatio: number,
  ): void {
    const horizontalSpeed = Math.hypot(this.state.velocity.x, this.state.velocity.z);
    const fractionRemaining = clamp01(
      (jump.gapEnd - sample.progress) / Math.max(1e-6, jump.gapEnd - jump.gapStart),
    );
    const travelDistance = jump.gapLength * fractionRemaining + jump.landingBuffer;
    const flightSpeed = Math.max(horizontalSpeed, jump.minimumLaunchSpeed, 1);
    const flightSeconds = clamp(travelDistance / flightSpeed, 0.22, 1.35);
    const effectiveGravity =
      GRAVITY + Math.max(0, this.physics.downforce) * speedRatio * 0.18;
    const landingDelta = jump.landingHeight - sample.position.y;
    const ballisticVelocity =
      (landingDelta + 0.18 + 0.5 * effectiveGravity * flightSeconds ** 2) /
      flightSeconds;

    // Retain the authored ramp's slope velocity, then add enough lift to make
    // the missing-road section readable. The ballistic target prevents uphill
    // landings from becoming impossible for the slower shipped cars.
    this.state.velocity.y = Math.max(
      this.state.velocity.y + jump.launchImpulse,
      ballisticVelocity,
    );
    this.state.verticalVelocity = this.state.velocity.y;
  }

  private roadStatus(nearest: TrackNearestPoint): { onRoad: boolean; inPlayableArea: boolean } {
    const width = Math.max(0.5, nearest.sample.width);
    const centerDistance =
      nearest.distanceToCenter ?? planarDistance(this.state.position, nearest.sample.position);
    const onRoad =
      nearest.sample.driveable !== false &&
      (nearest.onRoad ?? centerDistance <= width * 0.5 + 0.2);
    const playableMargin = Math.max(
      0,
      finiteOr(this.track.recovery?.playableMargin, DEFAULT_PLAYABLE_MARGIN),
    );
    const inPlayableArea = this.track.isWithinPlayableArea
      ? this.track.isWithinPlayableArea(this.state.position)
      : centerDistance <= width * 0.5 + playableMargin;
    return { onRoad, inPlayableArea };
  }

  private resolveBarrierCollision(nearest: TrackNearestPoint): boolean {
    const barriers = nearest.sample.barriers ?? 'none';
    if (barriers === 'none') return false;
    const right = nearest.sample.right ?? yawRight(this.state.yaw);
    const offset = {
      x: this.state.position.x - nearest.sample.position.x,
      y: this.state.position.y - nearest.sample.position.y,
      z: this.state.position.z - nearest.sample.position.z,
    };
    const lateralOffset = nearest.lateralOffset ?? dot(offset, right);
    const side = lateralOffset >= 0 ? 'right' : 'left';
    const hasBarrier = barriers === 'both' || barriers === side;
    const boundary = Math.max(0.5, nearest.sample.width * 0.5 - 0.42);
    const nearSurface = Math.abs(this.state.position.y - this.surfaceHeight(nearest.sample, this.state.position)) <= 2;
    if (!hasBarrier || Math.abs(lateralOffset) <= boundary || !nearSurface) return false;

    const targetOffset = Math.sign(lateralOffset || 1) * boundary;
    const correction = lateralOffset - targetOffset;
    this.state.position.x -= right.x * correction;
    this.state.position.y -= right.y * correction;
    this.state.position.z -= right.z * correction;

    const lateralVelocity = dot(this.state.velocity, right);
    if (Math.sign(lateralVelocity) === Math.sign(lateralOffset)) {
      // A soft arcade rebound communicates the wall without pinballing the car.
      this.state.velocity.x -= right.x * lateralVelocity * 1.28;
      this.state.velocity.y -= right.y * lateralVelocity * 1.28;
      this.state.velocity.z -= right.z * lateralVelocity * 1.28;
    }
    this.state.sliding = Math.max(this.state.sliding, 0.35);
    return true;
  }

  private updateRecovery(
    nearest: TrackNearestPoint,
    onRoad: boolean,
    inPlayableArea: boolean,
    deltaSeconds: number,
  ): void {
    this.onTrack = onRoad;
    this.inPlayableArea = inPlayableArea;
    this.offTrackSeconds = inPlayableArea ? 0 : this.offTrackSeconds + deltaSeconds;

    const horizontalSpeed = Math.hypot(this.state.velocity.x, this.state.velocity.z);
    const upDot = Math.cos(this.state.pitch) * Math.cos(this.state.roll);
    const invertedAndStopped = this.state.grounded && upDot < 0.15 && horizontalSpeed < 1.5;
    this.upsideDownSeconds = invertedAndStopped
      ? this.upsideDownSeconds + deltaSeconds
      : 0;

    if (onRoad) this.lastSafeSurfaceY = nearest.sample.position.y;
    if (this.recoverySignal) return;

    const configuredFallY = this.track.recovery?.fallY;
    const fell =
      (configuredFallY !== undefined && this.state.position.y < configuredFallY) ||
      this.state.position.y < this.lastSafeSurfaceY - RELATIVE_FALL_DISTANCE;
    if (fell) {
      this.setRecoverySignal('fell');
      return;
    }

    const offTrackGrace = Math.max(
      0,
      finiteOr(this.track.recovery?.offTrackGraceSeconds, DEFAULT_OFF_TRACK_GRACE),
    );
    if (this.offTrackSeconds >= offTrackGrace) {
      this.setRecoverySignal('left-playable-area');
      return;
    }

    const upsideDownGrace = Math.max(
      0,
      finiteOr(this.track.recovery?.upsideDownGraceSeconds, DEFAULT_UPSIDE_DOWN_GRACE),
    );
    if (this.upsideDownSeconds >= upsideDownGrace) {
      this.setRecoverySignal('upside-down');
    }
  }

  private setRecoverySignal(reason: VehicleRecoverySignal['reason']): void {
    this.recoverySignal = {
      reason,
      position: copyVector(this.state.position),
      progress: this.state.progress,
    };
  }

  private updateBoosterContacts(allowTrigger = true): void {
    const boosters = this.track.boosters;
    if (!boosters?.length) return;

    for (const booster of boosters) {
      const sample = this.track.sampleAt(booster.progress);
      const radius = Math.max(
        0.5,
        finiteOr(booster.radius ?? booster.triggerRadius, Math.max(2.5, sample.width * 0.35)),
      );
      const planar = planarDistance(this.state.position, sample.position);
      const vertical = Math.abs(this.state.position.y - this.surfaceHeight(sample, this.state.position));
      const inside = planar <= radius && vertical <= Math.max(2.5, radius * 0.5);
      const wasInside = this.boosterContacts.has(booster.id);

      if (inside && !wasInside) {
        this.boosterContacts.add(booster.id);
        if (allowTrigger) this.activateBooster(booster);
      } else if (!inside && wasInside) {
        this.boosterContacts.delete(booster.id);
      }
    }
  }

  private updateLaunchContacts(sample: TrackSurfaceSample): void {
    const activeJump = (sample as JumpSurfaceSample).jump;
    if (!activeJump) this.launchContacts.clear();
  }

  private settleLockedVehicle(nearest: TrackNearestPoint, onRoad: boolean): void {
    if (!onRoad || nearest.sample.driveable === false) return;
    this.state.position.y = this.surfaceHeight(nearest.sample, this.state.position);
    this.state.progress = nearest.progress;
    this.state.grounded = true;
    this.lastSafeSurfaceY = nearest.sample.position.y;
  }

  private snapToTrackIfClose(): void {
    const nearest = this.safeNearest(this.state.position, this.state.progress);
    const status = this.roadStatus(nearest);
    if (!status.onRoad) return;
    const targetY = this.surfaceHeight(nearest.sample, this.state.position);
    if (Math.abs(this.state.position.y - targetY) <= Math.max(1.5, this.rideHeight + 0.5)) {
      this.state.position.y = targetY;
      this.state.grounded = true;
      this.lastSafeSurfaceY = nearest.sample.position.y;
    }
  }

  private safeNearest(position: VectorState, hintProgress: number): TrackNearestPoint {
    const nearest = this.track.nearest(position, hintProgress);
    return {
      ...nearest,
      progress: clamp01(finiteOr(nearest.progress, hintProgress)),
      sample: {
        ...nearest.sample,
        progress: clamp01(finiteOr(nearest.sample.progress, nearest.progress)),
        width: Math.max(0.5, finiteOr(nearest.sample.width, 10)),
      },
    };
  }

  /** Height of the sampled banked road plane at the vehicle's x/z location. */
  private surfaceHeight(sample: TrackSurfaceSample, position: VectorState): number {
    const up = sample.up;
    if (!up || Math.abs(up.y) < 0.15) return sample.position.y + this.rideHeight;
    const offsetX = position.x - sample.position.x;
    const offsetZ = position.z - sample.position.z;
    return sample.position.y - (up.x * offsetX + up.z * offsetZ) / up.y + this.rideHeight;
  }

  private updateResult(steps: number): VehicleUpdateResult {
    return {
      steps,
      boosterTriggered: this.lastBoosterTriggered,
      landingImpact: this.lastLandingImpact,
      recovery: this.recoverySignal
        ? { ...this.recoverySignal, position: copyVector(this.recoverySignal.position) }
        : null,
    };
  }
}
